(() => {
  const ROOT_SELECTOR = '[data-bcc-root]';
  const CTA_SELECTOR = '[data-bcc-scroll-to-prices], [data-bela-colageno-scroll-to-offers]';
  const ANCHOR_ID = 'bela-colageno-complex-prices-start';
  const MOBILE_QUERY = '(max-width: 749px)';
  let observer;
  let easySellObserver;
  let whatsappCleanup;
  let initialized = false;

  function visible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function findOfferCards() {
    return Array.from(document.querySelectorAll('.offer-radio')).filter(visible);
  }

  function findOfferGroup() {
    const offers = findOfferCards();
    if (!offers.length) return null;
    if (offers.length === 1) return offers[0].parentElement || offers[0];

    let parent = offers[0].parentElement;
    while (parent && parent !== document.body) {
      if (offers.every((offer) => parent.contains(offer))) return parent;
      parent = parent.parentElement;
    }
    return offers[0].parentElement || offers[0];
  }

  function ensurePricesAnchor() {
    const group = findOfferGroup();
    if (!group) return null;

    let anchor = document.getElementById(ANCHOR_ID);
    if (!anchor) {
      anchor = document.createElement('div');
      anchor.id = ANCHOR_ID;
      anchor.setAttribute('aria-hidden', 'true');
    }

    // Some theme styles hide every aria-hidden element. Keep this anchor
    // visually empty but present in layout so scroll calculations are valid.
    anchor.style.cssText = 'display:block!important;height:0;width:100%;pointer-events:none;';

    if (anchor.nextElementSibling !== group) group.parentNode.insertBefore(anchor, group);
    return anchor;
  }

  function findPricesTarget() {
    return ensurePricesAnchor() ||
      findOfferGroup() ||
      document.querySelector('.es-qo-container') ||
      document.querySelector('#es-popup-button') ||
      document.querySelector('#bela-easysell-order');
  }

  function targetScrollTop(target) {
    const elementTop = target.getBoundingClientRect().top + window.scrollY;
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    const spaceAbove = isMobile
      ? Math.min(180, Math.max(120, window.innerHeight * 0.18))
      : Math.min(150, Math.max(80, window.innerHeight * 0.12));
    return Math.max(0, elementTop - spaceAbove);
  }

  function scrollToPrices() {
    const started = Date.now();
    const attempt = () => {
      const target = findPricesTarget();
      if (target && (target.id === ANCHOR_ID || visible(target))) {
        window.scrollTo({
          top: targetScrollTop(target),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
        window.setTimeout(() => {
          const stableTarget = findPricesTarget();
          if (stableTarget) {
            const expected = targetScrollTop(stableTarget);
            if (Math.abs(window.scrollY - expected) > 90) {
              window.scrollTo({ top: expected, behavior: 'auto' });
            }
          }
        }, 650);
        return;
      }
      if (Date.now() - started < 3500) window.setTimeout(attempt, 120);
    };
    attempt();
  }

  function bindCtas(scope = document) {
    scope.querySelectorAll(CTA_SELECTOR).forEach((button) => {
      if (button.dataset.bccBound === 'true') return;
      button.dataset.bccBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        scrollToPrices();
      });
    });
  }

  function initGallery(scope = document) {
    scope.querySelectorAll('[data-bcc-gallery]').forEach((gallery) => {
      if (gallery.dataset.bccReady === 'true') return;
      gallery.dataset.bccReady = 'true';
      const track = gallery.querySelector('[data-bcc-gallery-track]');
      const slides = Array.from(gallery.querySelectorAll('.bcc-gallery__slide'));
      const dots = Array.from(gallery.querySelectorAll('[data-bcc-gallery-dot]'));
      if (!track || slides.length < 2) return;

      const update = () => {
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.toggleAttribute('aria-current', dotIndex === index);
        });
      };
      let frame;
      track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      }, { passive: true });
      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        slides[index]?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }));

      // Keep the document scroll native until the visitor has clearly started
      // a horizontal swipe. This prevents an image touch from trapping a
      // vertical scroll while preserving the gallery swipe.
      let gesture = null;
      const gestureThreshold = 8;
      const endGesture = (event) => {
        if (gesture?.horizontal && track.hasPointerCapture?.(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
        gesture = null;
      };

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        gesture = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startScrollLeft: track.scrollLeft,
          horizontal: false,
          decided: false
        };
      }, { passive: true });

      track.addEventListener('pointermove', (event) => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const deltaX = event.clientX - gesture.startX;
        const deltaY = event.clientY - gesture.startY;

        if (!gesture.decided) {
          if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < gestureThreshold) return;
          gesture.decided = true;
          gesture.horizontal = Math.abs(deltaX) > Math.abs(deltaY);
          if (!gesture.horizontal) {
            gesture = null;
            return;
          }
          track.setPointerCapture?.(event.pointerId);
        }

        if (gesture?.horizontal) track.scrollLeft = gesture.startScrollLeft - deltaX;
      });

      track.addEventListener('pointerup', endGesture, { passive: true });
      track.addEventListener('pointercancel', endGesture, { passive: true });
      track.addEventListener('lostpointercapture', () => { gesture = null; }, { passive: true });
      update();
    });
  }

  function initLightbox(scope = document) {
    scope.querySelectorAll('[data-bcc-lightbox-src]').forEach((button) => {
      if (button.dataset.bccLightboxBound === 'true') return;
      button.dataset.bccLightboxBound = 'true';
      button.addEventListener('click', () => {
        const dialog = document.createElement('dialog');
        dialog.className = 'bcc-lightbox';
        dialog.setAttribute('aria-label', button.dataset.bccLightboxAlt || 'Imagen ampliada');
        dialog.innerHTML = '<button type="button" aria-label="Cerrar imagen ampliada">×</button><img alt="">';
        const image = dialog.querySelector('img');
        image.src = button.dataset.bccLightboxSrc;
        image.alt = button.dataset.bccLightboxAlt || '';
        dialog.querySelector('button').addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => dialog.remove(), { once: true });
        document.body.append(dialog);
        dialog.showModal();
      });
    });
  }

  function initSticky() {
    const sticky = document.querySelector('[data-bcc-sticky]');
    const hero = document.querySelector('.bcc-hero');
    if (!sticky || !hero || !window.matchMedia(MOBILE_QUERY).matches || !('IntersectionObserver' in window)) return;

    const refresh = () => {
      const purchaseTarget = findOfferGroup() || document.querySelector('#es-popup-button');
      const heroBottom = hero.getBoundingClientRect().bottom;
      const purchaseVisible = purchaseTarget && purchaseTarget.getBoundingClientRect().top < window.innerHeight * .88 &&
        purchaseTarget.getBoundingClientRect().bottom > window.innerHeight * .12;
      const show = heroBottom < 0 && !purchaseVisible;
      sticky.hidden = !show;
      document.body.classList.toggle('bcc-sticky-visible', show);
    };
    window.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    refresh();
  }

  function initWhatsAppOffset() {
    const hero = document.querySelector('.bcc-hero');
    const candidates = Array.from(document.querySelectorAll(
      'a[href*="wa.me"], a[href*="api.whatsapp.com"], [class*="whatsapp"] a[href]'
    ));
    const whatsapp = candidates.find(visible);
    if (!hero || !whatsapp || whatsapp.dataset.bccWhatsAppBound === 'true') return;

    whatsapp.dataset.bccWhatsAppBound = 'true';
    whatsapp.classList.add('bcc-whatsapp-managed');
    const update = () => {
      whatsapp.classList.toggle('bcc-whatsapp-after-hero', hero.getBoundingClientRect().bottom <= 0);
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();

    whatsappCleanup = () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      whatsapp.classList.remove('bcc-whatsapp-managed', 'bcc-whatsapp-after-hero');
      delete whatsapp.dataset.bccWhatsAppBound;
    };
  }

  function init() {
    if (initialized || !document.querySelector(ROOT_SELECTOR)) return;
    initialized = true;
    bindCtas();
    initGallery();
    initLightbox();
    initSticky();
    initWhatsAppOffset();
    ensurePricesAnchor();

    easySellObserver = new MutationObserver(() => {
      ensurePricesAnchor();
      bindCtas();
    });
    easySellObserver.observe(document.body, { childList: true, subtree: true });
  }

  function destroy() {
    observer?.disconnect();
    easySellObserver?.disconnect();
    whatsappCleanup?.();
    whatsappCleanup = undefined;
    document.body.classList.remove('bcc-sticky-visible');
    initialized = false;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    bindCtas(event.target);
    initGallery(event.target);
    initLightbox(event.target);
    ensurePricesAnchor();
  });
  document.addEventListener('shopify:section:unload', destroy);
})();
